import { useLocalStorage } from '@mantine/hooks'
import type { AuthSettings, TenantLoginInfo, UserAccountResponse } from '@types'
import {
  type AuthSession,
  type AuthSessionStorage,
  clearTokens,
  getUserMetaInfo,
} from '@utils/auth-utils'
import {
  authSessionShape,
  authSettings,
  deserializeLocalStorage,
  deserializeLocalStorageMap,
} from '@utils/localstorage-utils'
import type {
  AuthUser,
  FetchUserAttributesOutput,
  fetchAuthSession,
} from 'aws-amplify/auth'
import { useCallback, useMemo } from 'react'
import type { O } from 'ts-toolbelt'
import { z } from 'zod'

export const useAuthLocalStorage = () => {
  const [tenantId, setTenantId] = useLocalStorage<string | undefined>({
    defaultValue: undefined,
    deserialize: (value) =>
      deserializeLocalStorage(value, z.string().ulid(), undefined),
    getInitialValueInEffect: false,
    key: 'tenant-id',
  })

  const [ssoConfiguration, setSsoConfiguration] = useLocalStorage<
    TenantLoginInfo['authSettings'] | null
  >({
    defaultValue: null,
    deserialize: (value) => deserializeLocalStorage(value, authSettings, null),
    getInitialValueInEffect: false,
    key: 'CognitoIdentityServiceProvider.SsoConfiguration.v3',
  })

  const enteredViaSSO = useMemo(
    () => window.location.search.includes('code='),
    []
  )

  const [sessions, setSessions] = useLocalStorage<AuthSessionStorage>({
    defaultValue: {},
    deserialize: (value) => deserializeLocalStorageMap(value, authSessionShape),
    getInitialValueInEffect: false,
    key: 'CognitoIdentityServiceProvider.AuthSessions.v3',
  })

  const addSession = useCallback(
    (props: {
      account: Omit<UserAccountResponse, 'account'> & {
        account: O.Optional<UserAccountResponse['account'], 'userAttributes'>
      }
      authSettings?: AuthSettings
      fetchAuthSessionResult: Awaited<ReturnType<typeof fetchAuthSession>>
      providerName?: string
      user: AuthUser
      tokenUserAttributes: FetchUserAttributesOutput
    }) => {
      const username = props.user.username
      const userId = props.user.userId
      const meta = getUserMetaInfo(
        props.tokenUserAttributes,
        props.fetchAuthSessionResult,
        props.account.account
      )

      const updatedSession: AuthSession = {
        authenticated: true,
        authSettings: props.authSettings,
        lastRefreshTime: new Date().toISOString(),
        latestAccountInfo: props.account,
        meta,
        providerName: props.providerName,
        userId,
        username,
      }

      setSessions((previous) => ({
        ...previous,
        [username]: updatedSession,
      }))
    },
    [setSessions]
  )

  const removeSession = useCallback(
    ({ username }: { username: string }) => {
      clearTokens({ username })
      setSessions((previous) => {
        return {
          ...Object.fromEntries(
            Object.entries(previous).filter(([key]) => key !== username)
          ),
        }
      })
    },
    [setSessions]
  )

  const updateSession = useCallback(
    ({ username, data }: { data: Partial<AuthSession>; username: string }) => {
      setSessions((previous) => {
        const session = previous[username]
        if (!session) return previous

        const authenticated = data.authenticated ?? true

        return {
          ...previous,
          [username]: {
            ...session,
            ...data,
            authenticated,
            lastRefreshTime: new Date().toISOString(),
          },
        }
      })
    },
    [setSessions]
  )

  return {
    addSession,
    enteredViaSSO,
    removeSession,
    sessions: sessions ?? {},
    setSessions,
    setSsoConfiguration,
    setTenantId,
    ssoConfiguration,
    tenantId,
    updateSession,
  }
}
